import './base.css'
import { createTheme } from '@mui/material'
import themeJson from './theme'

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        header: true
        headerContained: true
    }
}

// A base theme is created with { cssVariables: true }. This generates
// the color tokens (e.g. --mui-palette-primary-main) that are used in the
// Figma exported theme file.
const baseTheme = createTheme(themeJson, {
    cssVariables: true
})

// The theme is then extended with additional customizations which can now
// utilize the tokenized colors.
const finalTheme = createTheme(baseTheme, {
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1072,
            xl: 1072
        }
    },
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    variants: [
                        {
                            props: { color: 'primary' },
                            style: {
                                backgroundColor: '#282725'
                            }
                        }
                    ]
                }
            }
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'header' },
                    style: {
                        'backgroundColor': 'transparent',
                        'borderWidth': 0,
                        'borderStyle': 'none',
                        'paddingLeft': '14px',
                        'paddingRight': '14px',
                        'fontFamily': 'Basis Grotesque Pro',
                        'fontWeight': 'bold',
                        'textTransform': 'none',
                        '&:not(.Mui-disabled):not(.Mui-focusVisible):hover': {
                            color: 'var(--mui-palette-primary-contrastText)'
                        }
                    }
                },
                {
                    props: { variant: 'headerContained' },
                    style: {
                        'backgroundColor': '#831A4A',
                        'borderWidth': 0,
                        'borderStyle': 'none',
                        'paddingLeft': '14px',
                        'paddingRight': '14px',
                        'fontFamily': 'Basis Grotesque Pro',
                        'fontWeight': 'bold',
                        'textTransform': 'none',
                        '&:not(.Mui-disabled):not(.Mui-focusVisible):hover': {
                            backgroundColor: '#6B143B'
                        }
                    }
                }
            ]
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '&.MuiSwitch-sizeMedium:has(.MuiSwitch-colorDefault)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '9px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(20px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(20px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-common-white_states-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeMedium:has(.MuiSwitch-colorPrimary)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '9px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(20px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(20px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-primary-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-primary-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeMedium:has(.MuiSwitch-colorError)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '9px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(20px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(20px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-error-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-error-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeMedium:has(.MuiSwitch-colorWarning)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '9px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(20px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(20px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-warning-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-warning-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeMedium:has(.MuiSwitch-colorInfo)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '9px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(20px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(20px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-info-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-info-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeMedium:has(.MuiSwitch-colorSuccess)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '9px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(20px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(20px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-success-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-success-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeMedium:has(.MuiSwitch-colorSecondary)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '9px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(20px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(20px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-secondary-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-secondary-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '58px',
                                'height': '38px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '12px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '20px',
                                        height: '20px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '34px',
                                        height: '14px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeSmall:has(.MuiSwitch-colorDefault)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '4px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(16px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(16px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-common-white_states-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeSmall:has(.MuiSwitch-colorPrimary)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '4px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(16px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(16px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-primary-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-primary-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeSmall:has(.MuiSwitch-colorError)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '4px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(16px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(16px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-error-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-error-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeSmall:has(.MuiSwitch-colorWarning)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '4px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(16px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(16px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-warning-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-warning-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeSmall:has(.MuiSwitch-colorInfo)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '4px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(16px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(16px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-info-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-info-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeSmall:has(.MuiSwitch-colorSuccess)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '4px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(16px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(16px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-success-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-success-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    },
                    '&.MuiSwitch-sizeSmall:has(.MuiSwitch-colorSecondary)': {
                        '& .MuiSwitch-switchBase': {
                            padding: '4px',
                            borderRadius: '100px',
                            background: 'none',
                            opacity: '1',
                            boxShadow: 'none'
                        },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform:
                                        'translateX(16px) translateY(0px)'
                                }
                            },
                        '&:has(.Mui-checked):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(16px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-secondary-main)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-secondary-main)',
                                        padding: '0',
                                        opacity: '0.5',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):has(.Mui-focusVisible)':
                            {
                                '& .MuiSwitch-switchBase': {
                                    transform: 'translateX(0px) translateY(0px)'
                                }
                            },
                        '&:not(:has(.Mui-checked)):not(:has(.Mui-disabled)):not(:has(.Mui-focusVisible))':
                            {
                                'width': '40px',
                                'height': '24px',
                                'transform': 'translateX(0px) translateY(0px)',
                                'padding': '7px',
                                'background': 'none',
                                'opacity': '1',
                                'borderRadius': '0',
                                'boxShadow': 'none',
                                '& .MuiSwitch-switchBase': {
                                    'transform':
                                        'translateX(0px) translateY(0px)',
                                    '& .MuiSwitch-thumb': {
                                        width: '16px',
                                        height: '16px',
                                        background:
                                            'var(--mui-palette-_components-switch-knobFillEnabled)',
                                        boxShadow: 'var(--mui-shadows-1)',
                                        padding: '0',
                                        opacity: '1',
                                        borderRadius: '50%'
                                    },
                                    '& + .MuiSwitch-track': {
                                        width: '26px',
                                        height: '10px',
                                        borderRadius: '100px',
                                        background:
                                            'var(--mui-palette-_components-switch-slideFill)',
                                        padding: '0',
                                        opacity: '0.38',
                                        boxShadow: 'none',
                                        transform:
                                            'translateX(0px) translateY(0px)'
                                    }
                                }
                            }
                    }
                }
            }
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    background: 'rgba(118, 84, 84, 0.2)'
                }
            }
        }
    }
})

export default finalTheme
